import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, MonitorSmartphone, RefreshCw, ShieldCheck, Trash2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { api } from '@/lib/api'
import { setCurrentUser } from '@/lib/auth'
import { ConfirmDialog } from '@/components/marketing/ConfirmDialog'

/**
 * Seguridad — sesiones activas (Sprint 16).
 *
 * Lista las sesiones server-side del usuario actual (la actual marcada),
 * con dispositivo (User-Agent legible), IP, fecha de creación y última
 * actividad. Permite revocar una sesión concreta o «Cerrar las demás».
 *
 * Revocar la sesión ACTUAL equivale a un logout: el backend limpia la cookie
 * y redirigimos a /login. Las sesiones mueren solas a los 30 min de
 * inactividad o a las 8 h de su creación (cap absoluto), lo que llegue antes.
 *
 * Sprint 19 (F9): las confirmaciones destructivas (revocar la sesión ACTUAL
 * y «Cerrar las demás») usan el ConfirmDialog Blueprint reutilizable en vez
 * de window.confirm. Revocar una sesión ajena sigue sin pedir confirmación
 * (no es destructivo para quien opera).
 */

/**
 * pendingForWiring — página nueva pendiente de cablear por el orquestador
 * (los agentes tienen prohibido tocar App.tsx / AppShell.tsx).
 *
 *   Ruta:     /marketing/security  (protegida, dentro de AuthGuard como el resto)
 *   Import:   import SecurityPage from '@/pages/marketing/SecurityPage'
 *   Nav item: «Seguridad» en AppShell, icono ShieldCheck (lucide-react,
 *             el mismo que usa la cabecera de esta página)
 */
export const pendingForWiring = {
  route: '/marketing/security',
  component: 'SecurityPage',
  navLabel: 'Seguridad',
  icon: 'ShieldCheck',
  protected: true,
} as const

interface SessionDto {
  id: string
  ip: string | null
  userAgent: string | null
  createdAt: string
  lastSeenAt: string
  expiresAt: string
  isCurrent: boolean
}

interface SessionsResponse {
  currentSessionId: string
  items: SessionDto[]
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * User-Agent → etiqueta legible «Navegador · SO». Heurística mínima sin
 * dependencias; si no reconoce nada, recorta el UA crudo. El UA completo
 * queda disponible en el title (tooltip).
 */
function formatUserAgent(ua: string | null): string {
  if (!ua) return 'Dispositivo desconocido'
  let browser = ''
  if (/edg\//i.test(ua)) browser = 'Edge'
  else if (/opr\/|opera/i.test(ua)) browser = 'Opera'
  else if (/firefox\//i.test(ua)) browser = 'Firefox'
  else if (/chrome\//i.test(ua)) browser = 'Chrome'
  else if (/safari\//i.test(ua) && /version\//i.test(ua)) browser = 'Safari'
  let os = ''
  if (/windows/i.test(ua)) os = 'Windows'
  else if (/iphone|ipad|ipod/i.test(ua)) os = 'iOS'
  else if (/mac os x|macintosh/i.test(ua)) os = 'macOS'
  else if (/android/i.test(ua)) os = 'Android'
  else if (/linux/i.test(ua)) os = 'Linux'
  if (browser && os) return `${browser} · ${os}`
  if (browser || os) return browser || os
  return ua.length > 60 ? `${ua.slice(0, 60)}…` : ua
}

export function SecurityPage() {
  const [data, setData] = useState<SessionsResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [busyOthers, setBusyOthers] = useState(false)
  // Confirmaciones pendientes (F9): sesión actual a revocar / cerrar demás.
  const [confirmCurrent, setConfirmCurrent] = useState<SessionDto | null>(null)
  const [confirmOthers, setConfirmOthers] = useState(false)
  const navigate = useNavigate()

  const load = useCallback(async () => {
    setErr(null)
    try {
      const r = await api<SessionsResponse>('/v1/marketing/auth/sessions')
      setData(r)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  function revoke(session: SessionDto) {
    if (session.isCurrent) {
      // ConfirmDialog Blueprint en vez de window.confirm (F9).
      setConfirmCurrent(session)
      return
    }
    void doRevoke(session)
  }

  async function doRevoke(session: SessionDto) {
    setBusyId(session.id)
    setErr(null)
    try {
      await api(`/v1/marketing/auth/sessions/${session.id}`, { method: 'DELETE' })
      if (session.isCurrent) {
        // El backend ya limpió la cookie; limpiamos el cache y a /login.
        setCurrentUser(null)
        navigate('/login', { replace: true })
        return
      }
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function revokeOthers() {
    setBusyOthers(true)
    setErr(null)
    try {
      await api('/v1/marketing/auth/sessions/revoke-others', { method: 'POST' })
      await load()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyOthers(false)
    }
  }

  const items = data?.items ?? []
  const others = items.filter((s) => !s.isCurrent)

  return (
    <div className="p-6 max-w-4xl">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="size-4 text-blueprint-700" aria-hidden="true" />
            <h1 className="text-ink-900 font-semibold text-lg">Seguridad · Sesiones</h1>
          </div>
          <p className="text-xs text-ink-700/70 mt-1 max-w-xl">
            Sesiones abiertas de tu cuenta. Cada sesión caduca a los 30 minutos de
            inactividad o a las 8 horas de abrirse, lo que llegue antes. Si algo no te
            cuadra, revócala y cambia la contraseña (eso cierra todas las demás).
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => void load()}
            className="bp-btn"
            title="Recargar la lista"
          >
            <RefreshCw className="size-3" aria-hidden="true" />
            Recargar
          </button>
          <button
            type="button"
            onClick={() => setConfirmOthers(true)}
            disabled={busyOthers || others.length === 0}
            aria-busy={busyOthers}
            className="bp-btn-primary"
            title="Revocar todas las sesiones excepto la actual"
          >
            <Trash2 className="size-3" aria-hidden="true" />
            {busyOthers ? 'Cerrando…' : 'Cerrar las demás sesiones'}
          </button>
        </div>
      </div>

      {err && (
        <div
          role="alert"
          className="flex items-start gap-2 text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5 mb-4"
        >
          <AlertTriangle className="size-3.5 mt-0.5 shrink-0" aria-hidden="true" />
          <span>{err}</span>
        </div>
      )}

      <div className="bp-card overflow-x-auto">
        {data === null && !err ? (
          <div className="p-6 text-xs font-mono text-ink-700/50" role="status">
            Cargando sesiones…
          </div>
        ) : items.length === 0 ? (
          <div className="p-6 text-xs font-mono text-ink-700/50">
            No hay sesiones activas.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-ink-700/60 border-b border-ink-900/10">
                <th className="px-3 py-2 font-medium">Dispositivo</th>
                <th className="px-3 py-2 font-medium">IP</th>
                <th className="px-3 py-2 font-medium">Creada</th>
                <th className="px-3 py-2 font-medium">Última actividad</th>
                <th className="px-3 py-2 font-medium">Caduca</th>
                <th className="px-3 py-2 font-medium text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`session-row-${s.id}`}
                  className="border-b border-ink-900/5 last:border-0"
                >
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <MonitorSmartphone
                        className="size-3.5 text-ink-700/50 shrink-0"
                        aria-hidden="true"
                      />
                      <span
                        className="text-ink-900"
                        title={s.userAgent ?? 'Sin User-Agent registrado'}
                      >
                        {formatUserAgent(s.userAgent)}
                      </span>
                      {s.isCurrent && (
                        <span
                          data-testid="session-current"
                          className="bp-pill bg-blueprint-50 text-blueprint-700"
                        >
                          actual
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-mono text-ink-700/80">{s.ip ?? '—'}</td>
                  <td className="px-3 py-2 text-ink-700/80">{fmtDate(s.createdAt)}</td>
                  <td className="px-3 py-2 text-ink-700/80">{fmtDate(s.lastSeenAt)}</td>
                  <td className="px-3 py-2 text-ink-700/80">{fmtDate(s.expiresAt)}</td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => revoke(s)}
                      disabled={busyId === s.id}
                      aria-busy={busyId === s.id}
                      className="bp-btn"
                      title={
                        s.isCurrent
                          ? 'Revocar tu sesión actual (equivale a cerrar sesión)'
                          : 'Revocar esta sesión'
                      }
                    >
                      <Trash2 className="size-3" aria-hidden="true" />
                      {busyId === s.id ? 'Revocando…' : 'Revocar'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {confirmCurrent && (
        <ConfirmDialog
          title="Revocar tu sesión actual"
          message="Esta es tu sesión ACTUAL: revocarla equivale a cerrar sesión y volverás a la pantalla de entrada. ¿Continuar?"
          confirmLabel="Revocar y salir"
          busy={busyId === confirmCurrent.id}
          onCancel={() => setConfirmCurrent(null)}
          onConfirm={() => {
            const session = confirmCurrent
            setConfirmCurrent(null)
            void doRevoke(session)
          }}
        />
      )}

      {confirmOthers && (
        <ConfirmDialog
          title="Cerrar las demás sesiones"
          message={`Se revocarán ${others.length === 1 ? 'la otra sesión abierta' : `las otras ${others.length} sesiones abiertas`} de tu cuenta. La sesión actual seguirá activa. ¿Continuar?`}
          confirmLabel="Cerrar las demás"
          busy={busyOthers}
          onCancel={() => setConfirmOthers(false)}
          onConfirm={() => {
            setConfirmOthers(false)
            void revokeOthers()
          }}
        />
      )}
    </div>
  )
}

export default SecurityPage
